import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Typography, Paper, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, Card, CardMedia, CardActions, CardContent, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth, API_BASE_URL, UPLOADS_BASE_URL } from '../../context/AuthContext';

export default function GalleryAdmin() {
  const [albums, setAlbums] = useState([]);
  const [openAlbumForm, setOpenAlbumForm] = useState(false);
  const [editingAlbumId, setEditingAlbumId] = useState(null);
  const [albumData, setAlbumData] = useState({ title: '' });
  const [uploading, setUploading] = useState(false);
  
  const { user } = useAuth();

  const fetchAlbums = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/albums.php`);
      const data = await response.json();
      if(Array.isArray(data)) setAlbums(data);
    } catch (error) {
      console.error("Errore fetch albums:", error);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);

  const handleOpenCreateAlbum = () => {
    setEditingAlbumId(null);
    setAlbumData({ title: '' });
    setOpenAlbumForm(true);
  };

  const handleOpenEditAlbum = (album) => {
    setEditingAlbumId(album.id);
    setAlbumData({ title: album.title });
    setOpenAlbumForm(true);
  };

  // Creazione / Modifica Album
  const handleSaveAlbum = async () => {
    const token = localStorage.getItem('adminToken');
    const method = editingAlbumId ? 'PUT' : 'POST';
    const payload = editingAlbumId ? { id: editingAlbumId, ...albumData } : albumData;
    try {
      const response = await fetch(`${API_BASE_URL}/albums.php`, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setOpenAlbumForm(false);
        setEditingAlbumId(null);
        setAlbumData({ title: '' });
        fetchAlbums();
      } else {
        alert("Errore durante il salvataggio dell'album.");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteAlbum = async (id) => {
    if (!window.confirm("Attenzione: l'eliminazione dell'album cancellerà anche tutte le foto al suo interno (solo nel database). Procedere?")) return;
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/albums.php`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id })
      });
      if (response.ok) fetchAlbums();
    } catch (error) {
      console.error(error);
    }
  };

  // Caricamento Immagini Automatico all'innesco della selezione
  const handleAutoUploadPhoto = async (e, albumId) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;
    
    const token = localStorage.getItem('adminToken');
    setUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("photo", file);
        formData.append("album_id", albumId);

        const response = await fetch(`${API_BASE_URL}/upload_photo.php`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (!response.ok) {
          alert(`Errore durante il caricamento di: ${file.name}`);
        }
      }
      fetchAlbums();
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset per consentire eventuale ricaricamento degli stessi file
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Eliminare questa foto?")) return;
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${API_BASE_URL}/photos.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: photoId })
      });
      if (response.ok) {
        fetchAlbums();
      } else {
        alert("Errore durante l'eliminazione della foto.");
      }
    } catch (error) {
      console.error('Delete photo error:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Gestione Galleria</Typography>
        <Button variant="contained" color="primary" onClick={handleOpenCreateAlbum}>
          + Crea Nuovo Album
        </Button>
      </Box>

      {albums.map((album) => (
        <Paper key={album.id} sx={{ p: 2, mb: 4, bgcolor: '#f9f9f9' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6">{album.title}</Typography>
              <Typography variant="caption" color="textSecondary">Cartella: {album.folder_name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Modifica titolo album">
                <IconButton color="primary" onClick={() => handleOpenEditAlbum(album)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Elimina intero album">
                <IconButton color="error" onClick={() => handleDeleteAlbum(album.id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Tooltip title="Puoi selezionare una o più foto contemporaneamente">
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<UploadFileIcon />}
                component="label"
                disabled={uploading}
              >
                {uploading ? 'Caricamento foto in corso...' : '+ Carica Foto'}
                <input 
                  type="file" 
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => handleAutoUploadPhoto(e, album.id)} 
                />
              </Button>
            </Tooltip>
          </Box>

          <Grid container spacing={2}>
            {album.images && album.images.map((photo) => (
              <Grid item xs={6} sm={4} md={3} key={photo.id}>
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={`${UPLOADS_BASE_URL}/${photo.image_url}`}
                    alt="Foto galleria"
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardActions sx={{ justifyContent: 'center' }}>
                    <Tooltip title="Elimina questa foto dall'album">
                      <Button size="small" color="error" onClick={() => handleDeletePhoto(photo.id)}>Elimina</Button>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
            {(!album.images || album.images.length === 0) && (
              <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>Nessuna foto presente in questo album.</Typography>
            )}
          </Grid>
        </Paper>
      ))}

      {/* Modale Creazione / Modifica Album */}
      <Dialog open={openAlbumForm} onClose={() => setOpenAlbumForm(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingAlbumId ? 'Modifica Titolo Album' : 'Nuovo Album'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal" fullWidth required
              label="Titolo Album (es. Cannero 2026)"
              value={albumData.title}
              onChange={(e) => setAlbumData({ title: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAlbumForm(false)}>Annulla</Button>
          <Button onClick={handleSaveAlbum} variant="contained">Salva</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
